import React from 'react';
import styled from 'styled-components';
import { IconArrowLeft as ArrowLeft } from '../icon/icon_arrow_left';
import { IconArrowRight as ArrowRight } from '../icon/icon_arrow_right';

export interface Props {
  startIndex: number;
  endIndex: number;
  total: number;
  pageSize: number;
  setPageSize: (pageSize: number) => void;
  hasPrevious: boolean;
  hasNext: boolean;
  previousPage: () => void;
  nextPage: () => void;
}

const Root = styled.div`
  display: flex;
  align-items: center;
`;

const PagingPanel = styled.div`
  align-items: center;
  display: flex;
  margin-right: 1.5rem;
`;

const NavButton = styled.button`
  align-items: center;
  display: flex;
  border: 1px solid #cdcdcd;
  padding: 0.3rem;

  &:hover {
    cursor: pointer;
  }
`;

const NavButtonBack = styled(NavButton)`
  border-top-left-radius: 0.3rem;
  border-bottom-left-radius: 0.3rem;
  margin-right: 0.4rem;
`;

const NavButtonForward = styled(NavButton)`
  border-top-right-radius: 0.3rem;
  border-bottom-right-radius: 0.3rem;
  margin-left: 0.4rem;
`;

const BoldLabel = styled.div`
  font-weight: bold;
  padding: 0 0.2rem;
`;

const arrowProps = {
  width: '.9rem',
  height: '.9rem',
  fill: '#444',
};

const Select = styled.select`
  font-size: 1rem;
  padding: 0.175rem 0.4rem;
`;

const getText = (props: Props) => {
  const {
    startIndex, endIndex, total, pageSize,
  } = props;

  // Handle when the number of results are less than the first page.
  if (total <= endIndex && total <= pageSize) {
    const result = total === 1 ? 'result' : 'results';
    return (
      <label>
        Showing
        {' '}
        {total}
        {' '}
        {result}
      </label>
    );
  }

  // Handle when total is less than the (pageSize*pageCount)
  const resolvedEndIndex = total <= endIndex ? total : endIndex;

  return (
    <>
      <label>Showing</label>
      <BoldLabel>{startIndex}</BoldLabel>
      <label>to</label>
      <BoldLabel>{resolvedEndIndex}</BoldLabel>
      of
      <BoldLabel>{total}</BoldLabel>
    </>
  );
};

export const Pagination = (props: Props) => (
  <Root>
    <PagingPanel>
      <NavButtonBack disabled={!props.hasPrevious} onClick={props.previousPage}>
        <ArrowLeft {...arrowProps} />
      </NavButtonBack>
      {getText(props)}
      <NavButtonForward
        disabled={!props.hasNext || props.total <= props.endIndex}
        onClick={props.nextPage}
      >
        <ArrowRight {...arrowProps} />
      </NavButtonForward>
    </PagingPanel>
    <Select
      value={props.pageSize}
      onChange={(e) => {
        props.setPageSize(Number(e.target.value));
      }}
    >
      {[10, 20, 30, 40, 50].map((pageSize) => (
        <option key={pageSize} value={pageSize}>
          Show
          {' '}
          {pageSize}
        </option>
      ))}
    </Select>
  </Root>
);
